import React, { useMemo } from "react";

import { useSelector } from "react-redux";
import { Field, FormSection, getFormValues } from "redux-form";
import {
	ReduxControls,
	ErrorBanner
} from "rhombus-react";

import DynamicAssetSelect from "social/DynamicAssetSelect";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import DynamicInput from "social/DynamicInput";
import DynamicSelect from "social/DynamicSelect";

import { getErrorMessagesByAdType } from "selectors/Social2";

import { LINKEDIN_CTAS } from "social/constants/CTA";
import {
	prettifyMessages,
	mapStringToOption,
	dynamicNormalizeOptionToString,
	dynamicMapStringToOption
} from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { LINKEDIN_VIDEO_CONSTRAINTS } from "social/validators/linkedIn";

import { ASSET_TYPES, CREATIVE_ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "../constants";

const {
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_INTRO,
	MAX_LENGTH_LANDING_PAGE,
	MIN_VIDEO_SEC,
	MAX_VIDEO_MIN,
	MIN_VIDEO_KB,
	MAX_VIDEO_MB,
	VIDEO_ACCEPT,
	MAX_THUMBNAIL_MB,
	THUMBNAIL_ACCEPT,
} = LINKEDIN_VIDEO_CONSTRAINTS;

const LINKEDIN_THUMBNAIL_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `<= ${MAX_THUMBNAIL_MB}MB`,
	},
	{
		name: "File Types:",
		description: THUMBNAIL_ACCEPT,
	},
];

export const LINKEDIN_VIDEO_FIELDS = {
	isDynamic,
	assetType,
	video: {
		name: "video",
		label: "Video",
		component: DynamicAssetSelect,
		defaultDynVarName: "video",
		className: "full-width",
		assetType: ASSET_TYPES.VIDEO,
		accept: VIDEO_ACCEPT,
		requirements: [
			{
				name: "File Size",
				description: `${MIN_VIDEO_KB}KB - ${MAX_VIDEO_MB}MB`,
			},
			{
				name: "File Types",
				description: VIDEO_ACCEPT,
			},
			{
				name: "Duration",
				description: `${MIN_VIDEO_SEC}s - ${MAX_VIDEO_MIN}m`,
			}
		],
		required: true,
	},
	thumbnail: {
		name: "thumbnail",
		label: "Thumbnail",
		className: "full-width",
		assetType: ASSET_TYPES.IMAGE,
		component: DynamicAssetSelect,
		defaultDynVarName: "video_thumbnail",
		requirements: LINKEDIN_THUMBNAIL_REQUIREMENTS,
		accept: THUMBNAIL_ACCEPT,
		tip: {
			direction: "top",
			description: "Video and thumbnail image must have the same aspect ratio",
		},
	},
	name: {
		name: "name",
		label: "Creative Name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		className: "full-width",
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
		required: true,
	},
	headline: {
		name: "headline",
		label: "Headline",
		placeholder: "Enter a short headline",
		component: DynamicInput,
		defaultDynVarName: "headline",
		tip: {
			description: "May get truncated after 70 characters",
			direction: "right",
		},
		className: "full-width",
		charMax: MAX_LENGTH_HEADLINE,
		// showCharCount: true,
		required: true,
	},
	intro: {
		name: "intro",
		label: "Intro",
		placeholder: "Tell people what your creative is about",
		component: DynamicInput,
		defaultDynVarName: "copy",
		tip: {
			description: "May get truncated after 150 characters",
			direction: "right",
		},
		className: "full-width",
		charMax: MAX_LENGTH_INTRO,
		// showCharCount: true,
		required: true,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: "clickTag1",
		className: "full-width",
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
		required: true,
	},
	cta: {
		name: "cta",
		label: "CTA",
		component: DynamicSelect,
		defaultDynVarName: "cta",
		options: LINKEDIN_CTAS.map(mapStringToOption),
		normalize: dynamicNormalizeOptionToString,
		format: dynamicMapStringToOption,
		required: true,
		className: "half-width",
		// normalize: normalizeOptionToString,
		// format: mapStringToOption,
		menuPlacement: "top",
		placeholder: "Select Call to Action Text",
		isClearable: false,
		variableNameInputTip: "Edit variable name",
	},
};

const adType = SOCIAL_AD_TYPES.LINKEDIN_VIDEO;

export default function LinkedInVideo(props){
	const { navigationActions, libraryId, isViewOnly, showPristineErrors, doVersionsExist } = props;

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const { assetType } = formValues[adType] || {};

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, LINKEDIN_VIDEO_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	let errorDisplay;
	if (hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />
		);
	}

	const $fields = Object.values(LINKEDIN_VIDEO_FIELDS).map((fieldProps) => {
		const extraProps = {};

		if (fieldProps.name === "video" || fieldProps.name === "thumbnail") {
			const isAdStudio = assetType === CREATIVE_ASSET_TYPES.ADSTUDIO;
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.isAdStudio = isAdStudio;
			extraProps.required = !isAdStudio;
		}

		extraProps.disabled = isViewOnly;

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			if (fieldProps.name !== "cta") {
				extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
			}
		}

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<div className="form-container">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{$fields}
				</div>
			</FormSection>
		</div>
	);
}

LinkedInVideo.displayName = "LinkedInVideoForm";
